import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { motion } from 'framer-motion';

const PrivacyPolicyPage = () => {
  const sections = [
    {
      title: "1. Introduction",
      content: `Gleam Guru Cleaning ("Company," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website, mobile applications, and services (collectively, the "Services").

By using our Services, you consent to the data practices described in this policy. If you do not agree with these practices, please do not use our Services.`
    },
    {
      title: "2. Information We Collect",
      content: `Personal Information You Provide:
• Name and contact information (email, phone number)
• Billing and service address(es)
• Payment information (processed securely via Stripe)
• Account credentials
• Communication preferences
• Service preferences and special instructions
• Photos (before/after cleaning, if applicable)

Information Collected Automatically:
• Device information (browser type, operating system)
• IP address and location data
• Usage data (pages visited, features used)
• Cookies and similar tracking technologies

Information from Third Parties:
• Payment verification from Stripe
• Background check results for contractors (not stored by us)
• Marketing analytics from service providers`
    },
    {
      title: "3. How We Use Your Information",
      content: `We use your information to:

• Provide and improve our cleaning services
• Process payments and send receipts
• Communicate about appointments and service updates
• Send promotional offers (with your consent)
• Respond to inquiries and support requests
• Verify identity and prevent fraud
• Comply with legal obligations
• Analyze usage to improve our Services
• Match you with appropriate cleaning professionals`
    },
    {
      title: "4. Information Sharing",
      content: `We may share your information with:

Service Providers:
• Payment processors (Stripe)
• Email service providers
• SMS notification services
• Analytics providers
• Cloud hosting services

Independent Contractors:
• Cleaners receive only the information necessary to perform your service (name, address, access instructions, service preferences)

Legal Requirements:
• When required by law or legal process
• To protect our rights and safety
• To prevent fraud or security threats

Business Transfers:
• In connection with a merger, acquisition, or sale of assets

We do not sell your personal information to third parties.`
    },
    {
      title: "5. Data Security",
      content: `We implement industry-standard security measures to protect your information:

• SSL/TLS encryption for data transmission
• Secure payment processing via Stripe (PCI-DSS compliant)
• Access controls and authentication
• Regular security assessments
• Employee training on data protection

While we strive to protect your information, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.`
    },
    {
      title: "6. Data Retention",
      content: `We retain your information for as long as:

• Your account is active
• Needed to provide Services
• Required for legal or business purposes
• Necessary to resolve disputes

After account closure, we may retain certain information for legal compliance, fraud prevention, and analytics purposes. Payment records are retained as required by tax and financial regulations.`
    },
    {
      title: "7. Your Rights and Choices",
      content: `You have the right to:

Access: Request a copy of your personal information
Correction: Update or correct inaccurate information
Deletion: Request deletion of your account and data
Opt-out: Unsubscribe from marketing communications
Portability: Receive your data in a portable format

To exercise these rights, contact us at ContactUs@GleamGurus.com

California Residents: You have additional rights under the CCPA, including the right to know what personal information is collected and the right to opt-out of sale (we do not sell personal information).`
    },
    {
      title: "8. Cookies and Tracking",
      content: `We use cookies and similar technologies to:

• Remember your preferences
• Analyze site traffic
• Improve user experience
• Deliver targeted advertising

Types of Cookies:
• Essential cookies (required for site function)
• Analytics cookies (usage statistics)
• Marketing cookies (advertising)

You can control cookies through your browser settings. Disabling certain cookies may affect site functionality.`
    },
    {
      title: "9. Third-Party Links",
      content: `Our Services may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies before providing any information.`
    },
    {
      title: "10. Children's Privacy",
      content: `Our Services are not intended for children under 18 years of age. We do not knowingly collect personal information from children. If we learn that we have collected information from a child under 18, we will delete it promptly.`
    },
    {
      title: "11. International Users",
      content: `Our Services are operated in the United States. If you access our Services from outside the US, your information may be transferred to, stored, and processed in the US, where data protection laws may differ from your country.`
    },
    {
      title: "12. Changes to This Policy",
      content: `We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last Modified" date. Significant changes may be communicated via email or site notification. Continued use of our Services after changes constitutes acceptance.`
    },
    {
      title: "13. Contact Us",
      content: `For questions or concerns about this Privacy Policy or our data practices, please contact us:

Gleam Guru Cleaning
Serving Portland Metro Area

Email: ContactUs@GleamGurus.com
Phone: (971) 715-0335

Data Protection Inquiries: privacy@gleamgurus.com`
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F56F05] via-[#F56F05] to-[#C2410C]" />
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px'}} />
        
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-heading mb-6 drop-shadow-lg">
              Privacy Policy
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Your privacy matters to us. Learn how we protect your information.
            </p>
          </motion.div>
        </div>
        
        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 space-y-8">
          {/* Effective Date */}
          <div className="text-center mb-12">
            <p className="text-zinc-600">Effective Date: February 1, 2026</p>
          </div>

          {sections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.03 }}
            >
              <Card className="border-0 shadow-lg overflow-hidden">
                <CardContent className="p-0">
                  {/* Orange accent line */}
                  <div className="h-1 bg-gradient-to-r from-[#F56F05] to-[#C2410C]" />
                  
                  <div className="p-6 md:p-8">
                    <h2 className="text-xl md:text-2xl font-bold text-zinc-900 font-heading mb-4">
                      {section.title}
                    </h2>
                    <div className="text-zinc-600 leading-relaxed whitespace-pre-line">
                      {section.content}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}

          {/* Last Updated */}
          <p className="text-center text-sm text-zinc-500 pt-8">
            Last updated: February 2026
          </p>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
